import { Injectable, OnDestroy } from '@angular/core';
import {
  FormControl,
  FormGroup,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { Subscription, take } from 'rxjs';
import { BusinessDataFormObject } from 'src/app/shared/models/business-data/business-data-form-object/business-data-form-object';
import { BusinessEquipmentFormObject } from 'src/app/shared/models/business-equipment/business-equipment-form-object/business-equipment-form-object';
import { BusinessEquipmentLabels } from 'src/app/shared/models/business-equipment/business-equipment-labels/business-equipment-labels';
import { BusinessRepresentativeFormObject } from 'src/app/shared/models/business-representative/business-representative-form-object/business-representative-form-object';
import { BusinessRepresentativeLabels } from 'src/app/shared/models/business-representative/business-representative-labels/business-representative-labels';
import {
  serialValidator,
  passwordValidator,
} from 'src/app/shared/validators/registration-validators';
import {
  noFutureDateValidator,
  sameValueTwoFields,
} from 'src/app/utils/form-validator';
import { TranslationService } from '../translation/translation.service';
import { UserService } from '../../api/user/user.service';
import { ProductService } from '../../api/product/product.service';
import { HashValidationDTO } from 'src/app/shared/models/dtos/responses/hash-validation-dto/hash-validation-dto';

export type TRegisterEquipmentFormGroup = FormGroup<TRegisterEquipmentForm>;

export type TRegisterEquipmentForm = {
  placeOfPurchase: FormControl<string | null>;
  installationDate: FormControl<Date | null>;
  businessName: FormControl<string | null>;
  representativeName: FormControl<string | null>;
  representativeLastName: FormControl<string | null>;
  businessEmail: FormControl<string | null>;
  country: FormControl<string | number | null>;
  stateOrRegion: FormControl<string | null>;
  primaryPhone: FormControl<string | null>;
  phoneType: FormControl<'mobile' | 'land' | null>;
  agree: FormControl<boolean | null>;
};

@Injectable({
  providedIn: 'root',
})
/**
 * Holds information about ongoing registration
 */
export class RegisterService implements OnDestroy {
  private _equipmentFormGroup!: UntypedFormGroup;
  activeStep: 1 | 2 | 3 = 1;
  private _businessDataFormObject!: BusinessDataFormObject;
  private _businessEquipmentFormObject!: BusinessEquipmentFormObject;
  private _businessRepresentativeFormObject!: BusinessRepresentativeFormObject;
  invitedRepresentativeEmail?: string;
  loading: boolean = false;
  readonly routingGuards = {
    expiredInvite: false,
  };
  businessHashValidation?: HashValidationDTO;
  errorMessages = {
    cantRegisterInvalidForm: 'cant-register-invalid-form-error',
    equipmentAlreadyExists: 'equipment-exists-error',
    errorTryingRegister: 'trying-register-user-error',
    cantInstatiateUser: 'cant-instantiate-user-error',
    candUpdateInvalidForm: 'cant-update-invalid-form-error',
    tryingUpdateBusinessError: 'trying-update-business-error',
  };
  private _registerEquipmentFormGroup!: TRegisterEquipmentFormGroup;
  private readonly subsink = new Subscription();

  /**
   * Register Equipment Form Group global instance
   */
  get registerEquipmentFormGroup() {
    return this._registerEquipmentFormGroup;
  }

  /**
   * @constructor
   */
  constructor(
    private userService: UserService,
    private productService: ProductService,
    private fb: UntypedFormBuilder,
    private translationService: TranslationService
  ) {
    this.initializeFormObjects();
    this.subsink.add(translationService.translateObject$(this.errorMessages));
  }

  /**
   * avoid memory leak (perhaps redundant)
   */
  ngOnDestroy() {
    this.subsink.unsubscribe();
  }

  /* eslint-disable-next-line */
  getBusinessEquipmentFormObject(
    page: number,
    pageSize: number,
    businessId?: number,
    completed?: (formGroup: UntypedFormGroup) => any
  ) {
    if (businessId == null) {
      return this._businessEquipmentFormObject;
    }
    this.productService
      .getBusinessEquipmentsPaginated(businessId, page, pageSize)
      .pipe(take(1))
      .subscribe((equipments) => {
        const formGroup = this._businessEquipmentFormObject.formGroup;
        const equipmentsFormArray = formGroup.get(
          'equipments'
        ) as UntypedFormArray;
        if (page === 0) {
          equipmentsFormArray.clear();
        }
        equipments.forEach((equipment) => {
          equipmentsFormArray.push(
            this.fb.group({
              serial: new UntypedFormControl(equipment.serial, [
                Validators.required,
                Validators.minLength(10),
                Validators.maxLength(18),
                serialValidator(),
              ]),
              planModel: new UntypedFormControl(equipment.planModel, [
                Validators.required,
              ]),
              id: new UntypedFormControl(equipment.id),
              note: new UntypedFormControl(equipment.note),
              registerDate: new UntypedFormControl(equipment.registerDate),
              familyName: new UntypedFormControl(
                equipment.familyResponseDTO?.name
              ),
            })
          );
        });
        if (completed != null) {
          completed(formGroup);
        }
      });
    return this._businessEquipmentFormObject;
  }

  /* eslint-disable-next-line */
  get businessRepresentativeFormObject() {
    return this._businessRepresentativeFormObject;
  }

  /* eslint-disable-next-line */
  get equipmentFormGroup() {
    return this._equipmentFormGroup;
  }

  /**
   * Resets all forms
   * @param {boolean} isUpdateBusiness
   */
  resetForms(isUpdateBusiness?: boolean) {
    this.initializeFormObjects(isUpdateBusiness);
  }

  /**
   * initialize form objects!
   * @param {boolean} _isUpdateBusiness
   */
  private initializeFormObjects(_isUpdateBusiness?: boolean) {
    this._equipmentFormGroup = this.businessEquipmentFormGroupFactory();
    this._businessEquipmentFormObject = this.businessEquipmentFormObjectFactory(
      this.equipmentFormGroup
    );
    this._registerEquipmentFormGroup = this.registerEquipmentFormGroupFactory();
  }

  /**
   * True if any of the forms are invalid
   * @return {boolean}
   */
  anyInvalidForm(): boolean {
    return this.equipmentFormGroup.invalid;
  }

  /**
   * Resets register equipment form group
   */
  resetRegisterEquipmentFormGroup() {
    this._registerEquipmentFormGroup = this.registerEquipmentFormGroupFactory();
  }

  /**
   * @return {TRegisterEquipmentFormGroup}
   */
  private registerEquipmentFormGroupFactory(): TRegisterEquipmentFormGroup {
    const registerEquipmentFormGroup = new FormGroup<TRegisterEquipmentForm>({
      placeOfPurchase: new FormControl('', Validators.minLength(3)),
      installationDate: new FormControl(null, noFutureDateValidator()),
      businessName: new FormControl(''),
      representativeName: new FormControl(''),
      representativeLastName: new FormControl(''),
      businessEmail: new FormControl('', Validators.email),
      country: new FormControl(''),
      stateOrRegion: new FormControl(''),
      primaryPhone: new FormControl('', [
        Validators.maxLength(18),
        Validators.minLength(9),
        Validators.pattern(/^\d*$/),
      ]),
      phoneType: new FormControl('mobile'),
      agree: new FormControl(false),
    });
    (
      Object.keys(
        registerEquipmentFormGroup.controls
      ) as (keyof TRegisterEquipmentForm)[]
    ).forEach((key) => {
      // eslint-disable-next-line security/detect-object-injection
      const control = registerEquipmentFormGroup.controls[key];
      if (key === 'agree') {
        control.addValidators(Validators.requiredTrue);
      } else {
        control.addValidators(Validators.required);
      }
      control.updateValueAndValidity();
    });

    return registerEquipmentFormGroup;
  }

  /**
   * Resets business equipment form group
   */
  resetBusinessEquipmentFormGroup() {
    this._businessEquipmentFormObject.formGroup =
      this.businessEquipmentFormGroupFactory();
  }

  /**
   * @return {FormGroup}
   */
  private businessEquipmentFormGroupFactory(): UntypedFormGroup {
    return new UntypedFormGroup({
      equipments: this.fb.array([RegisterService.createEquipmentFormTuple()]),
    });
  }

  /**
   * Creates a new equipment form tuple and returns it
   * @return {FormControl}
   */
  static createEquipmentFormTuple(): FormGroup<{
    serial: FormControl;
    planModel: FormControl;
    registerDate: FormControl<Date | null>;
    unremovable?: FormControl;
    id?: FormControl;
  }> {
    const serial = new FormControl('', [
      Validators.required,
      Validators.minLength(10),
      Validators.maxLength(18),
      Validators.pattern('^[a-zA-Z0-9]*$'),
      serialValidator(),
    ]);
    const planModel = new FormControl('');
    const registerDate = new FormControl<Date | null>(new Date());
    const equipmentFormTuple = new FormGroup({
      serial,
      planModel,
      registerDate,
    });
    let previousValue = serial.value;
    serial.valueChanges.subscribe((currentValue) => {
      if (previousValue !== currentValue) {
        planModel.setValue('');
      }
      previousValue = currentValue;
    });

    return equipmentFormTuple;
  }

  /**
   * @return {FormGroup}
   */
  private businessRepresentativeFormGroupFactory(): UntypedFormGroup {
    const freshFormGroup = new UntypedFormGroup({
      name: new UntypedFormControl('', [
        Validators.required,
        Validators.minLength(2),
      ]),
      lastName: new UntypedFormControl('', [Validators.required]),
      email: new UntypedFormControl('', [
        Validators.required,
        Validators.email,
      ]),
      password: new UntypedFormControl('', [
        Validators.required,
        passwordValidator(),
      ]),
      confirmPassword: new UntypedFormControl('', [Validators.required]),
      picture: new UntypedFormControl(''),
    });

    sameValueTwoFields(
      freshFormGroup.get('password')!,
      freshFormGroup.get('confirmPassword')!,
      [Validators.required]
    );

    return freshFormGroup;
  }

  /**
   * Builds business data form object
   * @param {FormGroup} formGroup
   * @return {BusinessEquipmentFormObject}
   */
  businessEquipmentFormObjectFactory(
    formGroup: UntypedFormGroup
  ): BusinessEquipmentFormObject {
    const equipmentLabels = new BusinessEquipmentLabels(
      'printer-serial-number',
      'model',
      'add-new-equipment',
      'remove-equipment',
      'invalid-serial-error-message',
      'serial-server-error',
      'equipment-exists-error'
    );

    this.subsink.add(this.translationService.translateObject$(equipmentLabels));

    return new BusinessEquipmentFormObject(equipmentLabels, formGroup);
  }

  /**
   * Builds business representative form object
   * @param {FormGroup} formGroup
   * @return {BusinessRepresentativeFormObject}
   */
  businessRepresentativeFormObjectFactory(
    formGroup: UntypedFormGroup
  ): BusinessRepresentativeFormObject {
    const businessRepresentativeLabels = new BusinessRepresentativeLabels(
      'name',
      'last-name',
      'email',
      'password',
      'confirm-password',
      'inform',
      'middle-header',
      'agree',
      'terms',
      ['upload-image', 'change-image'],
      ['alt1', 'alt2']
    );

    this.subsink.add(
      this.translationService.translateObject$(businessRepresentativeLabels)
    );

    return new BusinessRepresentativeFormObject(
      formGroup,
      businessRepresentativeLabels
    );
  }

  /**
   * @return {Observable<string>}
   */
  getTermsOfUseAndPrivacyConditionsURLs() {
    return this.userService.getTermsOfUseAndPrivacyConditionsURLs();
  }
}
